import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { lazy } from 'react';
import { useUID } from 'react-uid';
import { useMentionResource } from './providers/useMentionResource';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { PortalConsumer } from '../../../context';
import { gridSize } from '@atlaskit/theme/constants';
import { useUsers } from '../../../web-client/hooks/useUser';
import { FormStateChangeNotifier } from '../form';
var BITBUCKET_FIELD_ID = 'BitbucketMentions';
var AKFormField = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form/Field');
});
var AKUserPicker = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.user-picker' */
  '@atlaskit/user-picker');
});
var AKSmartUserPicker = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.smart-user-picker' */
  '@atlaskit/smart-user-picker');
});
var AKHelperMessage = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form').then(function (module) {
    return {
      default: module.HelperMessage
    };
  });
});
var AKErrorMessage = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form').then(function (module) {
    return {
      default: module.ErrorMessage
    };
  });
});
export var isProductKey = function isProductKey(productKey) {
  return productKey === 'confluence' || productKey === 'jira' || productKey === 'people' || productKey === 'compass' || productKey === 'bitbucket';
};
var validate = function validate(value) {
  return !value || Array.isArray(value) && value.length === 0 ? 'EMPTY' : undefined;
};

// Format the values(UserData) returned from useUsers hook, to pass to defaultValue prop
var hydrateDefaultValue = function hydrateDefaultValue(users) {
  var isMulti = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var defaultValues = [];
  for (var userId in users) {
    var _users$userId = users[userId],
      name = _users$userId.name,
      picture = _users$userId.picture;
    // Only grabbing the values where user name exist
    if (name) {
      defaultValues.push({
        name: name,
        avatarUrl: picture,
        id: userId,
        type: 'user'
      });
    }
  }
  return isMulti ? defaultValues : defaultValues[0];
};
var UserPicker = function UserPicker(_ref) {
  var name = _ref.name,
    label = _ref.label,
    description = _ref.description,
    placeholder = _ref.placeholder,
    isRequired = _ref.isRequired,
    isMulti = _ref.isMulti,
    _ref$defaultValue = _ref.defaultValue,
    defaultUsersValue = _ref$defaultValue === void 0 ? '' : _ref$defaultValue,
    _ref$includeUsers = _ref.includeUsers,
    includeUsers = _ref$includeUsers === void 0 ? true : _ref$includeUsers,
    _ref$includeGroups = _ref.includeGroups,
    includeGroups = _ref$includeGroups === void 0 ? false : _ref$includeGroups,
    client = _ref.client,
    mentionProvider = _ref.mentionProvider,
    currentAccountId = _ref.accountId,
    suppliedCloudId = _ref.cloudId,
    productKey = _ref.productKey,
    baseUrl = _ref.baseUrl;
  var query = useMentionResource(mentionProvider);
  var uid = useUID();

  // Hydrate the default value ourselves, since we are controlling value from <Field>
  // component value. It will get passed into the user picker as simply `value`.
  var accountIds = Array.isArray(defaultUsersValue) ? defaultUsersValue : [defaultUsersValue];
  var usersData = useUsers(accountIds, client);
  var hydratedDefaultValue = hydrateDefaultValue(usersData, isMulti);
  return /*#__PURE__*/React.createElement(PortalConsumer, null, function (portal) {
    return /*#__PURE__*/React.createElement(AKFormField, {
      id: "".concat(uid, "-forge-").concat(suppliedCloudId && productKey ? 'smart-user-picker' : 'user-picker'),
      name: name,
      label: label,
      defaultValue: hydratedDefaultValue,
      validate: isRequired ? validate : undefined,
      isRequired: isRequired
    }, function (_ref2) {
      var fieldProps = _ref2.fieldProps,
        error = _ref2.error;
      var userPickerProps = _objectSpread(_objectSpread({}, fieldProps), {}, {
        isMulti: isMulti,
        menuPortalTarget: portal || undefined,
        noOptionsMessage: function noOptionsMessage() {
          return null;
        },
        placeholder: placeholder,
        width: "".concat(gridSize() * 40, "px")
      });
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormStateChangeNotifier, {
        name: name,
        value: fieldProps.value
      }), suppliedCloudId && productKey ? /*#__PURE__*/React.createElement(AKSmartUserPicker, _extends({}, userPickerProps, {
        fieldId:
        // Bitbucket User Recommendations does not support generic contextType
        productKey === 'bitbucket' ? BITBUCKET_FIELD_ID : uid,
        debounceTime: 400,
        includeUsers: includeUsers,
        includeGroups: includeGroups,
        siteId: suppliedCloudId
        // If principalId === 'Context', upstream attempts to use other context
        // to discover the principal ID
        ,
        principalId: currentAccountId || 'Context',
        productKey: productKey,
        baseUrl: baseUrl,
        inputId: userPickerProps.id
      })) : /*#__PURE__*/React.createElement(AKUserPicker, _extends({}, userPickerProps, {
        loadOptions: query,
        fieldId: null,
        inputId: userPickerProps.id
      })), error === 'EMPTY' && /*#__PURE__*/React.createElement(AKErrorMessage, null, /*#__PURE__*/React.createElement(FormattedMessage, messages.emptyFieldErrorMessage)), description && /*#__PURE__*/React.createElement(AKHelperMessage, null, description));
    });
  });
};
export default UserPicker;
export var UserPickerFn = function UserPickerFn(_ref3) {
  var client = _ref3.client,
    mentionProvider = _ref3.mentionProvider,
    accountId = _ref3.accountId,
    cloudId = _ref3.cloudId,
    productKey = _ref3.productKey,
    baseUrl = _ref3.baseUrl,
    props = _ref3.props;
  var _ref4 = props,
    name = _ref4.name,
    label = _ref4.label,
    placeholder = _ref4.placeholder,
    description = _ref4.description,
    defaultValue = _ref4.defaultValue,
    isRequired = _ref4.isRequired,
    isMulti = _ref4.isMulti,
    includeUsers = _ref4.includeUsers,
    includeGroups = _ref4.includeGroups;
  return /*#__PURE__*/React.createElement(UserPicker, {
    client: client,
    accountId: accountId,
    cloudId: cloudId,
    productKey: productKey,
    mentionProvider: mentionProvider,
    name: name,
    label: label,
    placeholder: placeholder,
    description: description,
    defaultValue: defaultValue,
    isRequired: isRequired,
    isMulti: isMulti,
    includeUsers: includeUsers,
    includeGroups: includeGroups,
    baseUrl: baseUrl
  });
};

// ApolloClient, cloudId and productKey must be passed as props,
// and will not be fetched via context.
export var makeUserPicker = function makeUserPicker(_ref5) {
  var client = _ref5.client,
    mentionProvider = _ref5.mentionProvider,
    accountId = _ref5.accountId,
    cloudId = _ref5.cloudId,
    productKey = _ref5.productKey,
    baseUrl = _ref5.baseUrl;
  return function UserPickerNext(_ref6) {
    var props = _ref6.forgeDoc.props;
    return /*#__PURE__*/React.createElement(UserPickerFn, {
      props: props,
      client: client,
      mentionProvider: mentionProvider,
      accountId: accountId,
      cloudId: cloudId,
      productKey: productKey,
      baseUrl: baseUrl
    });
  };
};
var messages = defineMessages({
  emptyFieldErrorMessage: {
    id: 'confluence.user.picker.empty.field.error.message',
    defaultMessage: 'This field is required.',
    description: 'This message is displayed when a required text field is empty.'
  }
});